/**
 * `<ConsoleLogin>` — admin host login surface (console.francisquini.ai).
 *
 * Cerberus é proprietário: tem SPA própria de auth em id.francisquini.ai
 * (Etapa 07 do runbook), não usa Cognito Hosted UI. O console redireciona
 * pra essa SPA com `?return_to=` e o Cerberus volta com tokens no
 * `#access_token=...` hash fragment, que `consumeReturnFromUrl` (chamado
 * por <AuthProvider> em mount) processa.
 *
 * Mesmo padrão do `redirectToLogin()` em `lib/cerberus/redirect.ts`,
 * mas com CTA explícito (não auto-redirect) pra dar contexto visual:
 * o usuário SABE que vai sair pro IdP.
 */
import { useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { loginUrl } from '@/lib/cerberus/redirect'

const NEUTRAL_INK = '#0f172a'
const NEUTRAL_ACCENT = '#0f766e'

const wrap: CSSProperties = {
  minHeight: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f1f5f9',
  padding: '48px 24px',
}

const card: CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: 'white',
  borderRadius: 16,
  padding: '40px 32px',
  boxShadow: '0 8px 32px rgba(15, 23, 42, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  textAlign: 'center',
}

const primaryBtn: CSSProperties = {
  width: '100%',
  padding: '14px',
  borderRadius: 10,
  background: NEUTRAL_ACCENT,
  color: 'white',
  fontSize: 15,
  fontWeight: 700,
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
}

function getReturnTo(): string {
  if (typeof window === 'undefined') return '/'
  const params = new URLSearchParams(window.location.search)
  const raw = params.get('return_to')
  // Always send the IdP a fully-qualified URL on the SAME origin so the
  // hash-fragment bounce lands back on the console (and not on cuidari).
  const here = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const path = raw && raw.startsWith('/') ? raw : here.startsWith('/login') ? '/' : here
  return `${window.location.origin}${path === '/' ? '/' : path}`
}

export function ConsoleLogin() {
  const { t } = useTranslation('console')
  const [busy, setBusy] = useState(false)

  function onLogin() {
    setBusy(true)
    window.location.assign(loginUrl(getReturnTo()))
  }

  return (
    <main data-testid="console-login" aria-labelledby="console-login-heading" style={wrap}>
      <div style={card}>
        <h1
          id="console-login-heading"
          style={{
            fontSize: 22,
            fontWeight: 800,
            color: NEUTRAL_INK,
            letterSpacing: '-0.02em',
            margin: 0,
          }}
        >
          {t('login.title', 'Console interno')}
        </h1>
        <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.5, margin: 0 }}>
          {t(
            'login.subtitle',
            'Acesso restrito à operação do ecossistema. Autentique-se via Cerberus para continuar.',
          )}
        </p>
        <button
          type="button"
          onClick={onLogin}
          disabled={busy}
          data-testid="console-login-cta"
          style={{ ...primaryBtn, opacity: busy ? 0.6 : 1, cursor: busy ? 'wait' : 'pointer' }}
        >
          {busy ? t('login.redirecting', 'Redirecionando…') : t('login.cta', 'Entrar com Cerberus')}
        </button>
        <p style={{ fontSize: 11, color: '#64748b', margin: 0 }}>
          {t('login.footer', 'id.francisquini.ai')}
        </p>
      </div>
    </main>
  )
}
